import EventHubMixin from '@/mixins/EventHub.js'
import SizesMixin from '@/mixins/Sizes.js'
import ScrollMixin from '@/mixins/Scroll.js'
import TimeMixin from '@/mixins/Time.js'

export default
{
    name: 'app-line',

    mixins:
    [
        EventHubMixin,
        SizesMixin,
        ScrollMixin,
        TimeMixin
    ],

    created()
    {

    },

    mounted()
    {
        this.$canvas = this.$refs.canvas
        this.context = this.$canvas.getContext('2d')
        this.resizeThrottle = null
        this.strokes = []
        this.discs = []

        this.setLine()
        this.onResize()

        this.$eventHub.$on('line.moveTo', this.moveTo)
        this.$eventHub.$on('line.moveToElement', this.moveToElement)
        this.$eventHub.$on('line.setElement', this.setElement)
        this.$eventHub.$on('line.createStroke', this.createStroke)
        this.$eventHub.$on('line.deleteStroke', this.deleteStroke)
        this.$eventHub.$on('line.createDisc', this.createDisc)
        this.$eventHub.$on('line.deleteDisc', this.deleteDisc)
    },

    methods:
    {
        setLine()
        {
            this.line = {}
            this.line.acceleration = 0.04
            this.line.dumping = 0.75
            this.line.count = 0
            this.line.target = this.sizes.height * 0.5 + 100
            this.line.value = this.line.target
            this.line.speed = 0
            this.line.element = null
            this.line.points = []
        },

        onResize()
        {
            // Canvas
            this.$canvas.width = this.sizes.width
            this.$canvas.height = this.sizes.height

            // Count
            const targetAmplitude = 40
            this.line.count = Math.ceil(this.sizes.width / targetAmplitude) + 1
            this.line.amplitude = this.sizes.width / (this.line.count - 1)
            this.line.points = []

            for(let i = 0; i < this.line.count; i++)
            {
                const point = {}

                // Coordinates
                point.x = i * this.line.amplitude
                point.y = this.line.target

                this.line.points.push(point)
            }

            // Throttle resize
            if(this.line.element)
            {
                if(this.resizeThrottle)
                {
                    window.clearTimeout(this.resizeThrottle)
                }

                this.resizeThrottle = window.setTimeout(() =>
                {
                    this.moveToElement(true)
                }, 100)
            }
        },

        onTick()
        {
            this.draw()
        },

        moveTo(_y, _forced = false, _acceleration = 0.04)
        {
            // Same value
            if(this.line.target === _y && !_forced)
            {
                return
            }

            this.line.acceleration = _acceleration

            this.line.target = _y
        },

        setElement(_element = null, moveTo = false)
        {
            this.line.element = _element

            if(moveTo)
            {
                this.moveToElement()
            }
        },

        createStroke(_stroke = null)
        {
            if(typeof _stroke !== 'object')
            {
                return
            }

            _stroke.x = typeof _stroke.x !== 'undefined' ? _stroke.x : 0
            _stroke.length = typeof _stroke.length !== 'undefined' ? _stroke.length : 100
            _stroke.lineWidth = typeof _stroke.lineWidth !== 'undefined' ? _stroke.lineWidth : 3
            _stroke.strokeStyle = typeof _stroke.strokeStyle !== 'undefined' ? _stroke.strokeStyle : '#ff0000'
            _stroke.drawPoints = []
            _stroke.drawData = {}

            this.strokes.push(_stroke)
        },

        deleteStroke(_stroke = null)
        {
            const index = this.strokes.indexOf(_stroke)

            if(index !== - 1)
            {
                this.strokes.splice(index, 1)
            }
        },

        createDisc(_disc = null)
        {
            if(typeof _disc !== 'object')
            {
                return
            }

            _disc.x = typeof _disc.x !== 'undefined' ? _disc.x : 0
            _disc.radius = typeof _disc.radius !== 'undefined' ? _disc.radius : 5
            _disc.fillStyle = typeof _disc.fillStyle !== 'undefined' ? _disc.fillStyle : '#ff0000'
            _disc.drawPoint = {}
            _disc.drawData = {}

            this.discs.push(_disc)
        },

        deleteDisc(_disc = null)
        {
            const index = this.discs.indexOf(_disc)

            if(index !== - 1)
            {
                this.discs.splice(index, 1)
            }
        },

        moveToElement(_force = false, _acceleration = 0.04)
        {
            if(!this.line.element || !document.body.contains(this.line.element))
            {
                return
            }

            this.moveTo(this.line.element.getBoundingClientRect().top + this.scroll.current, _force, _acceleration)
        },

        draw()
        {
            let needsUpdate = false

            /**
             * Update line
             */
            const delta = this.line.target - this.line.value
            this.line.speed += delta * this.line.acceleration
            this.line.speed *= this.line.dumping
            this.line.value += this.line.speed
            this.line.rounded = Math.round(this.line.value * 10) / 10

            // Update each point but the first
            for(let i = this.line.count - 1; i > 0; i--)
            {
                const _point = this.line.points[i]
                const y = this.line.points[i - 1].y

                if(_point.y !== y)
                {
                    needsUpdate = true
                }

                _point.y = y
            }

            // Update first point
            this.line.points[0].y = this.line.rounded


            /**
             * Update strokes
             */

            for(const _stroke of this.strokes)
            {
                if(_stroke.x + _stroke.length > 0 && _stroke.x < this.sizes.width)
                {
                    const drawData = {
                        x: _stroke.x,
                        length: _stroke.length,
                        lineWidth: _stroke.lineWidth,
                        strokeStyle: _stroke.strokeStyle
                    }

                    // Test if something changed
                    if(
                        drawData.x !== _stroke.drawData.x ||
                        drawData.length !== _stroke.drawData.length ||
                        drawData.lineWidth !== _stroke.drawData.lineWidth ||
                        drawData.strokeStyle !== _stroke.drawData.strokeStyle ||
                        needsUpdate
                    )
                    {
                        _stroke.drawData = drawData
                        needsUpdate = true
                        const drawPoints = []

                        // First point
                        drawPoints.push(this.getInterpolatePoint(_stroke.x))

                        // Intermediar points
                        for(const _point of this.line.points)
                        {
                            if(_point.x > _stroke.x && _point.x < _stroke.x + _stroke.length)
                            {
                                drawPoints.push(_point)
                            }
                        }

                        // Last point
                        drawPoints.push(this.getInterpolatePoint(_stroke.x + _stroke.length))

                        _stroke.drawPoints = drawPoints
                    }
                }
            }

            /**
             * Update discs
             */

            for(const _disc of this.discs)
            {
                const drawData = {
                    x: _disc.x,
                    radius: _disc.radius,
                    fillStyle: _disc.fillStyle
                }

                // Test if something changed
                if(
                    drawData.x !== _disc.drawData.x ||
                    drawData.radius !== _disc.drawData.radius ||
                    drawData.fillStyle !== _disc.drawData.fillStyle ||
                    needsUpdate
                )
                {
                    // Interpolate
                    _disc.drawPoint = this.getInterpolatePoint(_disc.x)

                    _disc.drawData = drawData
                    needsUpdate = true
                }
            }

            /**
             * Draw if needs update
             */
            if(needsUpdate)
            {
                // Clear
                this.context.clearRect(0, 0, this.sizes.width, this.sizes.height)

                // Draw line
                this.context.strokeStyle = '#ffffff'
                this.context.globalAlpha = 0.3
                this.context.lineWidth = 1
                this.context.beginPath()

                for(let i = this.line.count - 1; i >= 0; i--)
                {
                    const _point = this.line.points[i]

                    // First => move to
                    if(i === this.line.count - 1)
                    {
                        this.context.moveTo(_point.x, Math.round(_point.y) + 0.5)
                    }

                    // Others => line to
                    else
                    {
                        this.context.lineTo(_point.x, Math.round(_point.y) + 0.5)
                    }
                }

                this.context.stroke()

                // Draw strokes
                for(const _stroke of this.strokes)
                {
                    this.context.strokeStyle = _stroke.strokeStyle
                    this.context.globalAlpha = 1
                    this.context.lineWidth = _stroke.lineWidth
                    this.context.beginPath()

                    for(let i = 0; i < _stroke.drawPoints.length; i++)
                    {
                        const _point = _stroke.drawPoints[i]

                        // First => move to
                        if(i === 0)
                        {
                            this.context.moveTo(_point.x, Math.round(_point.y) + 0.5)
                        }

                        // Others => line to
                        else
                        {
                            this.context.lineTo(_point.x, Math.round(_point.y) + 0.5)
                        }
                    }

                    this.context.stroke()
                }

                // Draw discs
                for(const _disc of this.discs)
                {
                    this.context.fillStyle = _disc.fillStyle
                    this.context.globalAlpha = 1

                    this.context.beginPath()
                    this.context.arc(_disc.drawPoint.x, _disc.drawPoint.y, _disc.radius, 0, Math.PI * 2)
                    this.context.fill()
                }
            }
        },

        getInterpolatePoint(_x)
        {
            let pointBefore = this.line.points.reverse().find((_point) => _point.x <= _x)

            if(typeof pointBefore === 'undefined')
            {
                pointBefore = this.line.points[this.line.points.length - 1]
            }

            let pointAfter = this.line.points.reverse().find((_point) => _point.x > _x)

            if(typeof pointAfter === 'undefined')
            {
                pointAfter = this.line.points[this.line.points.length - 1]
            }

            let ratio = (_x - Math.floor(_x / this.line.amplitude) * this.line.amplitude) / this.line.amplitude
            if(ratio < 0)
            {
                ratio = 1
            }

            const interpolatePoint = {
                x: pointBefore.x + (pointAfter.x - pointBefore.x) * ratio,
                y: pointBefore.y + (pointAfter.y - pointBefore.y) * ratio
            }

            return interpolatePoint
        }
    }
}



// WEBPACK FOOTER //
// ./src/components/app-line/script.js